import * as usuariosApi from "@/api/usuariosApi";
import * as clientesApiService from "@/services/clientesApiService";

/**
 * Crea o actualiza un perfil de usuario en Supabase
 * @param {Object} userData - Datos del usuario
 * @param {string} userData.clerkId - ID de Clerk
 * @param {string} userData.email - Email del usuario
 * @param {string} userData.nombre - Nombre completo
 * @param {string} userData.telefono - Teléfono del usuario
 * @param {string} userData.tipoUsuario - Tipo de usuario ('Admin' o 'Cliente')
 * @returns {Promise<Object>} - Datos del usuario actualizado
 */
export const createOrUpdateUserProfile = async (userData) => {
  try {
    //console.log("Buscando usuario por ClerkId:", userData.clerkId);
    
    // Intentar encontrar usuario existente por ClerkId
    const existingUser = await getUserByClerkId(userData.clerkId);
    
    if (existingUser) {
      /* //console.log("Usuario encontrado, actualizando:", existingUser.id);
      //console.log("🔍 Verificando datos para creación de cliente:", {
        tipoUsuario: userData.tipoUsuario,
        telefono: userData.telefono,
        email: userData.email,
        clerkId: userData.clerkId
      }); */
      
      // Si es un cliente, verificar que tenga un cliente asociado
      if (userData.tipoUsuario === 'Cliente' && userData.telefono) {
        /* //console.log("🔧 Usuario existente - Verificando cliente asociado"); */
        try {
          let cliente = await clientesApiService.getClienteByClerkId(userData.clerkId);
          
          if (!cliente) {
            /* //console.log("🔧 Usuario existente - Cliente no encontrado, creando cliente"); */
            cliente = await clientesApiService.createCliente({
              nombre: userData.nombre,
              telefono: userData.telefono,
              email: userData.email,
              clerk_id: userData.clerkId
            });
            ////console.log("✅ Usuario existente - Cliente creado:", cliente);
          } else {
            ////console.log("✅ Usuario existente - Cliente ya existe:", cliente.id);
          }
        } catch (clienteError) {
          console.error("❌ Usuario existente - Error al verificar/crear cliente:", clienteError);
        }
      }
      
      // Si el usuario existe, actualizar sus datos
      return await updateUser(existingUser.id, userData);
    } else {
      //console.log("Usuario no encontrado, creando nuevo usuario");
      
      // Si no existe, crear nuevo usuario con protección contra duplicados
      try {
        return await createUser(userData);
      } catch (createError) {
        // Si falla por duplicado, intentar buscar de nuevo
        if (createError.message && createError.message.includes('duplicate')) {
          //console.log("Detected duplicate error, trying to fetch existing user again");
          const retryUser = await getUserByClerkId(userData.clerkId);
          if (retryUser) {
            //console.log("Found existing user after duplicate error, checking if client needs to be created");
            
            // Asegurar que el cliente se cree aunque el usuario ya exista
            if (userData.tipoUsuario === 'Cliente' && userData.telefono) {
              try {
                //console.log("🔧 Retry - Verificando cliente para usuario existente");
                let cliente = await clientesApiService.getClienteByClerkId(userData.clerkId);
                
                if (!cliente) {
                  //console.log("🔧 Retry - Cliente no encontrado, creando cliente para usuario existente");
                  cliente = await clientesApiService.createCliente({
                    nombre: userData.nombre,
                    telefono: userData.telefono,
                    email: userData.email,
                    clerk_id: userData.clerkId
                  });
                  //console.log("✅ Retry - Cliente creado para usuario existente:", cliente);
                }
              } catch (clienteError) {
                console.error("❌ Retry - Error al crear cliente para usuario existente:", clienteError);
              }
            }
            
            return retryUser;
          }
        }
        throw createError;
      }
    }
  } catch (error) {
    console.error("Error en createOrUpdateUserProfile:", error);
    throw error;
  }
};

/**
 * Busca un usuario por su ID de Clerk
 * @param {string} clerkId - ID de Clerk
 * @returns {Promise<Object|null>} - Usuario encontrado o null
 */
export const getUserByClerkId = async (clerkId) => {
  try {
    return await usuariosApi.getUsuarioPorClerkId(clerkId);
  } catch (error) {
    console.error("Error buscando usuario por ClerkId:", error);
    throw error;
  }
};

/**
 * Crea un nuevo usuario en Supabase
 * @param {Object} userData - Datos del usuario
 * @returns {Promise<Object>} - Usuario creado
 */
const createUser = async (userData) => {
  try {
    /* //console.log("🔧 createUser - Iniciando creación de usuario:", userData.clerkId); */
    
    // Generar datos para el usuario
    const newUserData = {
      nombre: userData.nombre,
      email: userData.email,
      clerk_id: userData.clerkId,
      tipo_usuario: userData.tipoUsuario || 'Cliente',
      activo: true
    };
    
    /* //console.log("🔧 createUser - Datos del usuario a crear:", newUserData); */
    
    // Crear usuario usando la API de Supabase
    const usuario = await usuariosApi.createUsuario(newUserData);
    ////console.log("✅ createUser - Usuario creado exitosamente:", usuario.id);
    ////console.log("✅ createUser - Objeto usuario completo:", usuario);
    
    // Si es un cliente, crear o actualizar el registro en la tabla de clientes
    /* //console.log("🔧 createUser - Verificando creación de cliente:", {
      tipoUsuario: userData.tipoUsuario,
      telefono: userData.telefono,
      shouldCreateClient: userData.tipoUsuario === 'Cliente' && userData.telefono
    }); */
    
    if (userData.tipoUsuario === 'Cliente' && userData.telefono) {
      /* //console.log("🔧 createUser - Procesando cliente para usuario tipo Cliente"); */
      try {
        // Buscar cliente existente por clerk_id
        /* //console.log("🔍 createUser - Buscando cliente existente por clerk_id:", userData.clerkId); */
        let cliente = await clientesApiService.getClienteByClerkId(userData.clerkId);
        
        // Si no existe, crear un nuevo cliente vinculado al clerk_id
        if (!cliente) {
          //console.log("🔧 createUser - Cliente no encontrado, creando nuevo cliente");
          cliente = await clientesApiService.createCliente({
            nombre: userData.nombre,
            telefono: userData.telefono,
            email: userData.email,
            clerk_id: userData.clerkId  // Nueva relación
          });
          ////console.log("✅ createUser - Cliente creado con clerk_id:", cliente);
        } else {
          ////console.log("🔧 createUser - Cliente encontrado, actualizando datos");
          // Actualizar cliente existente
          cliente = await clientesApiService.updateCliente(cliente.id, {
            nombre: userData.nombre,
            telefono: userData.telefono,
            email: userData.email
          });
          ////console.log("✅ createUser - Cliente actualizado:", cliente);
        }
      } catch (clienteError) {
        console.error("❌ createUser - Error al procesar cliente:", clienteError);
        // Continuamos aunque falle la creación del cliente
      }
    } else {
      ////console.log("ℹ️ createUser - No es cliente o falta teléfono, saltando creación de cliente");
    }
    
    return usuario;
  } catch (error) {
    console.error("❌ createUser - Error en createUser:", error);
    throw error;
  }
};

/**
 * Actualiza un usuario existente en Supabase
 * @param {string} userId - ID del usuario a actualizar
 * @param {Object} userData - Datos actualizados del usuario
 * @returns {Promise<Object>} - Usuario actualizado
 */
const updateUser = async (userId, userData) => {
  try {
    // Datos a actualizar
    const updateData = {
      nombre: userData.nombre
    };
    
    // Si el usuario es cliente y proporciona teléfono, intentar actualizar el cliente
    if (userData.tipoUsuario === 'Cliente' && userData.telefono) {
      try {
        // Buscar el usuario actual
        const usuario = await usuariosApi.getUsuarioById(userId);
        
        if (usuario && usuario.cliente_id) {
          // Si tiene cliente_id, actualizar ese cliente
          await clientesApiService.updateCliente(usuario.cliente_id, {
            nombre: userData.nombre,
            telefono: userData.telefono,
            email: userData.email
          });
        } else {
          // Si no tiene cliente_id, buscar por email o crear uno nuevo
          let cliente = await clientesApiService.getClienteByEmail(userData.email);
          
          if (!cliente) {
            cliente = await clientesApiService.createCliente({
              nombre: userData.nombre,
              telefono: userData.telefono,
              email: userData.email
            });
          } else {
            // Actualizar el cliente existente
            await clientesApiService.updateCliente(cliente.id, {
              nombre: userData.nombre,
              telefono: userData.telefono
            });
          }
          
          // Guardar cliente_id en el usuario
          if (cliente && cliente.id) {
            updateData.cliente_id = cliente.id;
          }
        }
      } catch (clienteError) {
        console.error("Error al procesar cliente durante actualización:", clienteError);
        // Continuamos con la actualización del usuario aunque falle la del cliente
      }
    }
    
    // Actualizar usuario usando la API de Supabase
    return await usuariosApi.updateUsuario(userId, updateData);
  } catch (error) {
    console.error("Error en updateUser:", error);
    throw error;
  }
};

/**
 * Obtiene un usuario por su email
 * @param {string} email - Email del usuario
 * @returns {Promise<Object|null>} - Usuario encontrado o null
 */
export const getUserByEmail = async (email) => {
  try {
    return await usuariosApi.getUsuarioPorEmail(email);
  } catch (error) {
    console.error("Error buscando usuario por email:", error);
    throw error;
  }
};